import { css } from '@emotion/core'
import { usePageView } from '@fs/zion-analytics'
import { Button, Cell, Checkbox, Grid } from '@fs/zion-ui'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import { getAccessControl, updateAccessControl } from '../../../services/personService'
import getUserSummary from '../../../services/userService'
import { isAlphaNumericCisId, isAlphaNumericTreeId } from '../../../util/EntityUtil'
import objectByPath from '../../../util/ObjectUtil'
import { getIdElement } from '../../../util/StringUtil'
import PersonOwnerPicker from '../../common/PersonOwnerPicker'
import { PermissionsContext } from '../../app/PermissionsContext'
import { PersonContext } from '../PersonContext'
import ConfirmChangePrompt from '../summary/ConfirmChangePrompt'
import { getDmcTree } from '../../../services/treeService'

const floatRight = css`
  float: right;
`

export default function PersonAccessControl() {
  usePageView({ page_detail: 'DMConsole:PersonAccessControl' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.person.accessControl'))
  const { error } = useNotifications()
  const { currentPerson, fetchCurrentPerson } = useContext(PersonContext)
  const { isRole } = useContext(PermissionsContext)
  const personId = currentPerson.id
  const [loading, setLoading] = useState(false)
  const [editable, setEditable] = useState(false)
  const [accessControl, setAccessControl] = useState()
  const [initialState, setInitialState] = useState()
  const [owner, setOwner] = useState({
    ownerCisId: '',
    ownerTreeId: '',
    ownerName: '',
    ownerTreeType: '',
    idType: '',
    input: '',
  })
  const [hidden, setHidden] = useState()
  const [locked, setLocked] = useState()
  const [restricted, setRestricted] = useState()
  const [russianData, setRussianData] = useState()
  const [hideCompletedOrdinances, setHideCompletedOrdinances] = useState()
  const [ordinanceApprovalRequired, setOrdinanceApprovalRequired] = useState()
  const [unpublished, setUnpublished] = useState()
  const [changeMessage, setChangeMessage] = useState('')
  // DATA-12446: tree contributors can set `unpublished` themselves, so every admin level sees the
  // flag, but only Data Admin 4/5/6 may change it -- gated on top of the page-wide edit toggle.
  const canEditUnpublished = isRole(['Data Admin 4', 'Data Admin 5', 'Data Admin 6'])

  const setPageState = (data) => {
    if (!data) return
    const ac = data
    const originalHidden = ac?.hidden || false
    const originalLocked = ac?.locked || false
    const originalRestricted = ac?.ownerRestricted || false
    const originalRussiaData = ac?.russianData || false
    const originalHideCompletedOrdinances = ac?.hideCompletedOrdinances || false
    const originalOrdinanceApprovalRequired = ac?.ordinanceApprovalRequired || false
    const originalUnpublished = ac?.unpublished || false
    ac.idType = ac.owner.ownerTreeId ? 'treeId' : 'cisId'
    setAccessControl(ac)
    setHidden(originalHidden)
    setLocked(originalLocked)
    setRestricted(originalRestricted)
    setRussianData(originalRussiaData)
    setHideCompletedOrdinances(originalHideCompletedOrdinances)
    setOrdinanceApprovalRequired(originalOrdinanceApprovalRequired)
    setUnpublished(originalUnpublished)
  }

  useEffect(() => {
    if (personId) {
      getAccessControl(personId)
        .then((response) => {
          if (response.status === 200 && response.data) {
            setInitialState(response.data)
            setPageState(response.data)
          }
        })
        .catch((err) => {
          error(
            t('person.errors.gettingAccessControl', {
              defaultValue: `Error getting Access Control for person: ${err.message || ''}`,
              message: err.message || '',
            }),
            err
          )
        })
    }
  }, [error, personId, t])

  const setOwnerData = async (ac) => {
    if (ac) {
      let ownerCisId = ac.owner.ownerCisId
      const ownerTreeId = ac.owner.ownerTreeId
      const ownerName = ac.owner.ownerName
      const ownerTreeType = ac.owner.tfTreeType
      const idType = ac.idType
      if (ownerName?.toLowerCase() === 'admin') {
        ownerCisId = 'ADMIN'
      }
      if (ownerName?.toLowerCase() === 'public') {
        ownerCisId = 'PUBLIC'
      }
      setOwner({
        ...owner,
        ownerCisId,
        ownerTreeId,
        ownerName,
        ownerTreeType,
        idType,
        input: ac.idType === 'cisId' ? ownerCisId : ownerTreeId,
      })
    }
  }
  useEffect(() => {
    setOwnerData(accessControl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessControl])

  const reset = () => {
    setPageState(initialState)
    setChangeMessage('')
    if (accessControl) {
      setOwnerData(accessControl)
    }
  }

  function updateAccessControlAndDispatch(updateRequest) {
    updateAccessControl(personId, updateRequest)
      .then(() => {
        fetchCurrentPerson()
        getAccessControl(personId)
          .then((response) => {
            if (response.status === 200 && response.data) {
              setAccessControl(response.data)
              setInitialState(response.data)
            }
          })
          .catch((err) => {
            error(
              t('person.errors.gettingAccessControl', {
                defaultValue: `Error getting Access Control for person: ${err.message || ''}`,
                message: err.message || '',
              }),
              err
            )
          })
      })
      .catch((err) => {
        let detailedMsg = err.message || ''
        const msgObj = objectByPath(err, 'response.data.notifications[0].msg')

        if (msgObj) {
          detailedMsg = msgObj
        }
        const msg = detailedMsg.match(/\[{"error":"(.*)"}]/)
        detailedMsg = (msg && msg[1]) || detailedMsg

        error(
          t('person.errors.updatingAccessControl', {
            defaultValue: `Error updating Access Control for person: ${detailedMsg}`,
            message: detailedMsg,
          }),
          err
        )
      })
  }

  const saveChanges = () => {
    const updateRequest = {
      ownerCisId: owner.idType === 'cisId' ? owner.ownerCisId : undefined,
      treeId: owner.idType === 'treeId' ? owner.ownerTreeId : undefined,
      hidden,
      locked,
      ownerRestricted: restricted,
      russianData,
      hideCompletedOrdinances,
      ordinanceApprovalRequired,
      unpublished,
      attribution: {
        changeMessage,
      },
    }
    updateAccessControlAndDispatch(updateRequest)
  }

  const toggleEdit = () => {
    setEditable(!editable)
    reset()
  }

  const getTextLabel = () => {
    if (editable) {
      if (owner.idType === 'cisId') {
        return t('person.summary.makeLivingPersonDialog.ownerCisId', 'New Owner CIS ID')
      }
      return t('person.summary.makeLivingPersonDialog.ownerTreeId', 'New Owner Tree ID')
    }
    if (owner.idType === 'cisId') {
      return t('owner.ownerCisId', 'Owner CIS ID')
    }
    return t('owner.ownerTreeId', 'Owner Tree ID')
  }

  return (
    <>
      <Grid>
        <Cell columns={3}>
          <Grid>
            <Cell>
              <h4>{t('owner.label', 'Owner')}</h4>
            </Cell>
            <Cell>
              {accessControl && (
                <PersonOwnerPicker
                  disabled={!editable}
                  loading={loading}
                  textLabel={getTextLabel()}
                  radioLabel={editable ? t('chooseIDType', 'Choose ID type') : t('ownerCidIdPicker.idType', 'ID type')}
                  owner={owner}
                  updateIdType={(e) => {
                    const newIdType = e.target.value
                    setOwner({ ...owner, idType: newIdType })
                  }}
                  updateId={async (e) => {
                    const newId = e.target.value
                    if (owner && (owner.idType === 'cisId' || owner.idType === '')) {
                      // we set this immediately so it shows up as soon as possible in the component
                      setOwner({
                        ...owner,
                        ownerCisId: '',
                        ownerName: '',
                        ownerTreeType: '',
                        idType: 'cisId',
                        input: newId,
                      })
                      const newIdWithoutPrefix = getIdElement(newId)
                      if (
                        isAlphaNumericCisId(newIdWithoutPrefix) &&
                        !newId.startsWith('cis.wkca') &&
                        !newId.startsWith('cis.prod')
                      ) {
                        try {
                          let ownerName = ''
                          setLoading(true)
                          const userResponse = await getUserSummary(newIdWithoutPrefix, 'cis')
                          const user = userResponse.data
                          if (user?.dmcUser?.username) {
                            ownerName = user?.dmcUser?.contactName
                          }
                          setOwner({
                            ...owner,
                            ownerCisId: user?.dmcUser?.cisId,
                            ownerName,
                            ownerTreeType: '',
                            idType: 'cisId',
                            input: newId,
                          })
                        } catch (err) {
                          error(`Could not find user for ${newId} (${JSON.stringify(err, null, 2)})`, err)
                        }
                        setLoading(false)
                      } else if (newIdWithoutPrefix === 'ADMIN' || newIdWithoutPrefix === 'PUBLIC') {
                        setOwner({
                          ...owner,
                          ownerCisId: newIdWithoutPrefix,
                          ownerName: '',
                          ownerTreeType: '',
                          idType: 'cisId',
                          input: newId,
                        })
                      }
                    } else if (owner && (owner.idType === 'treeId' || owner.idType === '')) {
                      // we set this immediately so it shows up as soon as possible in the component
                      setOwner({
                        ...owner,
                        ownerTreeId: '',
                        ownerName: '',
                        ownerTreeType: '',
                        idType: 'treeId',
                        input: newId,
                      })
                      let ownerName = ''
                      let ownerTreeType = ''
                      if (isAlphaNumericTreeId(newId)) {
                        try {
                          setLoading(true)
                          const dmcTree = await getDmcTree(newId)
                          ownerName = dmcTree.data.tree.name
                          ownerTreeType = dmcTree.data.treeType
                          setOwner({
                            ...owner,
                            ownerTreeId: newId,
                            ownerName,
                            ownerTreeType,
                            idType: 'treeId',
                            input: newId,
                          })
                        } catch (err) {
                          error(`Could not find group for ${newId} (${JSON.stringify(err, null, 2)})`, err)
                        }
                        setLoading(false)
                      }
                    }
                  }}
                />
              )}
            </Cell>
          </Grid>
        </Cell>
        <Cell columns={3}>
          {hidden !== undefined &&
            locked !== undefined &&
            restricted !== undefined &&
            russianData !== undefined &&
            hideCompletedOrdinances !== undefined &&
            ordinanceApprovalRequired !== undefined &&
            unpublished !== undefined && (
              <Grid>
                <Cell columns={6}>
                  <h4>{t('accessControl.flags', 'Flags')}</h4>
                </Cell>
                <Cell columns={6}>
                  <Checkbox
                    id="hiddenCheck"
                    disabled={!editable}
                    label={t('hidden', 'Hidden')}
                    checked={hidden}
                    onChange={(e) => setHidden(e.target.checked)}
                    name="hidden-chb"
                  />
                </Cell>
                <Cell columns={6}>
                  <Checkbox
                    id="lockedCheck"
                    disabled={!editable}
                    label={t('accessControl.locked', 'Locked')}
                    checked={locked}
                    onChange={(e) => setLocked(e.target.checked)}
                    name="locked-chb"
                  />
                </Cell>
                <Cell columns={6}>
                  <Checkbox
                    id="ownerRestrictedCheck"
                    disabled={!editable}
                    label={t('accessControl.ownerRestricted', 'Owner Restricted')}
                    checked={restricted}
                    onChange={(e) => setRestricted(e.target.checked)}
                    name="owner-restricted-chb"
                  />
                </Cell>
                <Cell columns={6}>
                  <Checkbox
                    id="hideCompletedOrdinancesCheck"
                    disabled={!editable}
                    label={t('accessControl.hideCompletedOrds', 'Hide Completed Ordinances')}
                    checked={hideCompletedOrdinances}
                    onChange={(e) => setHideCompletedOrdinances(e.target.checked)}
                    name="hide-ords-chb"
                  />
                </Cell>
                <Cell columns={6}>
                  <Checkbox
                    id="ordinanceApprovalRequiredCheck"
                    disabled={!editable}
                    label={t('accessControl.ordApprovalRequired', 'Ordinance Approval Required')}
                    checked={ordinanceApprovalRequired}
                    onChange={(e) => setOrdinanceApprovalRequired(e.target.checked)}
                    name="oar-chb"
                  />
                </Cell>
                <Cell columns={6}>
                  <Checkbox
                    id="unpublishedCheck"
                    disabled={!editable || !canEditUnpublished}
                    label={t('accessControl.UNPUBLISHED', 'Unpublished')}
                    checked={unpublished}
                    onChange={(e) => setUnpublished(e.target.checked)}
                    name="unpublished-chb"
                  />
                </Cell>
                <Cell columns={6}>
                  <Checkbox
                    id="russianDataCheck"
                    disabled
                    label={t('accessControl.russianData', 'Russian Data')}
                    checked={russianData}
                    onChange={(e) => setRussianData(e.target.checked)}
                    name="russian-data-chb"
                  />
                </Cell>
              </Grid>
            )}
        </Cell>
        <Cell columns={2}>
          <div>
            <span css={floatRight}>
              <Button id={editable ? 'stopEditingBtn' : 'editBtn'} emphasis="medium" onClick={toggleEdit}>
                {editable ? 'Stop Editing' : 'Edit'}
              </Button>
            </span>
          </div>
        </Cell>
      </Grid>

      <ConfirmChangePrompt
        id="accessControlChange"
        open={editable}
        setOpen={setEditable}
        changeMessage={changeMessage}
        onChange={(event) => setChangeMessage(event.target.value)}
        onSave={saveChanges}
        disabled={loading}
      />
    </>
  )
}
